import { useEffect, useState } from "react";
import api from "../services/api";

const blobUrlCache = new Map<string, string>();
const pendingBlobRequests = new Map<string, Promise<string>>();

/**
 * Fetches an attachment file as a blob and returns an object URL for use in
 * <img src>. Blob URLs are cached per (attachmentId, variant) to avoid
 * redundant network fetches across re-renders.
 *
 * GET /api/attachments/{id}/file|thumbnail requires auth — cannot be used as a
 * bare <img src> since auth headers are required. This hook handles that by
 * fetching with Axios (which attaches the Bearer token) and creating a
 * temporary blob URL.
 *
 * @param variant "original" fetches from /file; "thumbnail" fetches from /thumbnail.
 *   Defaults to "original".
 */
export function useAttachmentUrl(
  attachmentId: string | null | undefined,
  variant: "original" | "thumbnail" = "original",
  _retryKey = 0,
): {
  url: string | null;
  loading: boolean;
} {
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!attachmentId) {
      setUrl(null);
      setLoading(false);
      return;
    }

    let cancelled = false;

    const endpoint =
      variant === "thumbnail"
        ? `/attachments/${attachmentId}/thumbnail`
        : `/attachments/${attachmentId}/file`;
    const cacheKey = `${attachmentId}:${variant}`;

    const cachedUrl = blobUrlCache.get(cacheKey);
    if (cachedUrl) {
      setUrl(cachedUrl);
      setLoading(false);
      return;
    }

    setLoading(true);
    const pendingRequest =
      pendingBlobRequests.get(cacheKey) ??
      api.get(endpoint, { responseType: "blob" }).then((response) => {
        const nextUrl = URL.createObjectURL(response.data as Blob);
        blobUrlCache.set(cacheKey, nextUrl);
        return nextUrl;
      });

    if (!pendingBlobRequests.has(cacheKey)) {
      pendingBlobRequests.set(cacheKey, pendingRequest);
    }

    pendingRequest
      .then((nextUrl) => {
        if (!cancelled) {
          setUrl(nextUrl);
        }
      })
      .catch(() => {
        if (!cancelled) setUrl(null);
      })
      .finally(() => {
        pendingBlobRequests.delete(cacheKey);
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [attachmentId, variant, _retryKey]);

  return { url, loading };
}
